import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../../supabase';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import {
  FiRefreshCw,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiUser,
  FiHash,
  FiLayers,
  FiCheckCircle,
  FiAlertTriangle,
  FiDownload,
} from 'react-icons/fi';
import '../styles/AdminShared.css';
import '../styles/newCoupon.css';

const formatDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return String(val);
  return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

const formatEndTime = (activatedAt, totalDuration) => {
  if (!activatedAt || !totalDuration) return '—';
  const start = new Date(activatedAt);
  if (isNaN(start)) return '—';
  return formatDate(start.getTime() + totalDuration * 1000);
};

const statusBadge = (status, stampsLeft) => {
  const isUsed = status === 'deactivated' || stampsLeft === 0;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      background: isUsed ? '#fef2f2' : '#f0fdf4',
      color: isUsed ? '#dc2626' : '#15803d',
      border: `1px solid ${isUsed ? '#fecaca' : '#bbf7d0'}`,
    }}>
      {isUsed ? <FiAlertTriangle size={11} /> : <FiCheckCircle size={11} />}
      {isUsed ? 'Brukt ut' : 'Aktiv'}
    </span>
  );
};

export default function KlippekortOversikt() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'used'
  const [expanded, setExpanded] = useState({}); // rowId -> bool
  const [sortBy, setSortBy] = useState('purchasedDesc');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: kkRows, error: kkError } = await supabase
        .from('klippekort')
        .select('*, orders(order_reference), users(name, email, phone), klippekort_redemptions(*)')
        .order('date_purchased', { ascending: false });
      if (kkError) throw kkError;

      const allRows = (kkRows || []).map(kk => {
        const stampTotal = kk.stamp_amounts || 0;
        const stampUsed = kk.used_stamps || 0;
        const stampsLeft = stampTotal - stampUsed;

        const redeemHistory = (kk.klippekort_redemptions || [])
          .map(r => ({
            id: r.id,
            datePurchased: r.used_at || r.activated_at,
            usedStamps: r.used_stamps,
            duration: r.duration,
            activatedAt: r.activated_at,
            activatedFrom: r.activated_from,
            totalDuration: r.total_duration,
          }))
          .sort((a, b) => new Date(b.datePurchased || 0) - new Date(a.datePurchased || 0));

        return {
          _rowId: kk.id,
          userEmail: kk.users?.email || '—',
          displayName: kk.users?.name || kk.users?.email || kk.users?.phone || 'Ukjent',
          userPhone: kk.users?.phone || '—',
          orderReference: kk.orders?.order_reference || kk.order_id,
          kkDocId: kk.id,
          name: kk.name || '—',
          productId: kk.product_id || kk.id || '—',
          stampTotal,
          stampUsed,
          stampsLeft,
          status: stampsLeft <= 0 ? 'deactivated' : 'active',
          datePurchased: kk.date_purchased,
          lastDayOfUse: null,
          usedDates: redeemHistory.map(r => r.datePurchased).filter(Boolean),
          redeemHistory,
        };
      });

      setRows(allRows);
    } catch (err) {
      setError('Feil ved henting av data: ' + err.message);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    let result = rows;
    if (filter === 'active') result = result.filter(r => r.status !== 'deactivated' && r.stampsLeft > 0);
    if (filter === 'used') result = result.filter(r => r.status === 'deactivated' || r.stampsLeft <= 0);

    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(r =>
        r.userEmail.toLowerCase().includes(q) ||
        r.displayName.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.orderReference.toLowerCase().includes(q) ||
        r.productId.toLowerCase().includes(q)
      );
    }

    result = [...result].sort((a, b) => {
      if (sortBy === 'purchasedDesc') return new Date(b.datePurchased || 0) - new Date(a.datePurchased || 0);
      if (sortBy === 'purchasedAsc')  return new Date(a.datePurchased || 0) - new Date(b.datePurchased || 0);
      if (sortBy === 'stampsLeft')    return b.stampsLeft - a.stampsLeft;
      if (sortBy === 'userAsc')       return a.userEmail.localeCompare(b.userEmail);
      return 0;
    });

    return result;
  }, [rows, filter, search, sortBy]);

  const totalActive = rows.filter(r => r.status !== 'deactivated' && r.stampsLeft > 0).length;
  const totalUsed   = rows.filter(r => r.status === 'deactivated' || r.stampsLeft <= 0).length;
  const totalStampsRedeemed = rows.reduce((s, r) => s + r.stampUsed, 0);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const exportCSV = () => {
    const headers = ['Eier','E-post','Telefon','Ordre','Produkt','Total','Brukt','Igjen','Status','Kjøpt','Utløper'];
    const csvRows = [
      headers.join(';'),
      ...filtered.map(r => [
        r.displayName, r.userEmail, r.userPhone, r.orderReference,
        r.name, r.stampTotal, r.stampUsed, r.stampsLeft,
        r.status === 'deactivated' ? 'Brukt ut' : 'Aktiv',
        formatDate(r.datePurchased), r.lastDayOfUse || '—',
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))
    ];
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `klippekort-oversikt-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      <AdminDashboardNav />
      <div className="admin-page-container">

        {/* Header */}
        <div className="coupon-header">
          <div>
            <h1><span style={{ color: '#010f3c', display: 'inline-flex', marginRight: 12, verticalAlign: 'middle' }}><FiLayers /></span>Klippekort Oversikt</h1>
            <p className="coupon-subtitle">Oversikt over alle klippekort, eiere og brukt-historikk. Bruk for å avdekke misbruk.</p>
          </div>
          <div className="coupon-header-stats">
            <div className="coupon-stat-pill">
              <FiLayers style={{ fontSize: 14 }} />
              {rows.length} Totalt
            </div>
            <div className="coupon-stat-pill" style={{ color: '#15803d' }}>
              <FiCheckCircle style={{ fontSize: 14 }} />
              {totalActive} Aktive
            </div>
            <div className="coupon-stat-pill" style={{ color: '#dc2626' }}>
              <FiAlertTriangle style={{ fontSize: 14 }} />
              {totalUsed} Brukt ut
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Totalt klippekort', value: rows.length, color: '#6B4FBB', bg: '#f3f0ff' },
            { label: 'Aktive', value: totalActive, color: '#15803d', bg: '#f0fdf4' },
            { label: 'Brukt ut', value: totalUsed, color: '#dc2626', bg: '#fef2f2' },
            { label: 'Klipp løst ut', value: totalStampsRedeemed, color: '#d97706', bg: '#fffbeb' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}22`, borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div className="card">
          {/* Toolbar */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: '1 1 220px' }}>
              <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Søk på eier, e-post, produkt, ordre…"
                style={{ width: '100%', paddingLeft: 38, padding: '10px 12px 10px 38px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'Poppins', boxSizing: 'border-box' }}
              />
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 6 }}>
              {[['all','Alle'],['active','Aktive'],['used','Brukt ut']].map(([val, lbl]) => (
                <button key={val} onClick={() => setFilter(val)} style={{
                  padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: filter === val ? '#010f3c' : '#fff', color: filter === val ? '#fff' : '#64748b',
                  fontFamily: 'Poppins',
                }}>{lbl}</button>
              ))}
            </div>

            {/* Sort */}
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'Poppins', background: '#fff' }}>
              <option value="purchasedDesc">Nyeste kjøp</option>
              <option value="purchasedAsc">Eldste kjøp</option>
              <option value="stampsLeft">Flest klipp igjen</option>
              <option value="userAsc">Bruker A–Å</option>
            </select>

            {/* Actions */}
            <button className="btn btn-secondary btn-sm" onClick={fetchData} disabled={loading}>
              <FiRefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : {}} /> Oppdater
            </button>
            <button className="btn btn-primary btn-sm" onClick={exportCSV} disabled={filtered.length === 0}>
              <FiDownload size={14} /> Eksporter CSV
            </button>
          </div>

          {/* Error */}
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', color: '#dc2626', marginBottom: 16, fontSize: 14 }}>
              {error}
            </div>
          )}

          {/* Loading */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
              <FiRefreshCw size={28} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: 12 }}>Henter klippekort fra alle brukere…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
              <FiLayers size={32} />
              <p style={{ marginTop: 12 }}>Ingen klippekort funnet.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e2e8f0' }}>
                    {['Eier','Ordre','Klippekort','Brukt / Total','Igjen','Status','Kjøpt',''].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(row => {
                    const isOpen = expanded[row._rowId];
                    const suspicious = row.usedDates.length !== row.stampUsed;
                    return (
                      <React.Fragment key={row._rowId}>
                        <tr style={{ borderBottom: '1px solid #f1f5f9', background: isOpen ? '#fafaff' : '#fff', transition: 'background 0.15s' }}>
                          {/* Owner */}
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FiUser size={14} style={{ color: '#6B4FBB' }} />
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.displayName}</div>
                                <div style={{ fontSize: 12, color: '#94a3b8' }}>{row.userEmail}</div>
                              </div>
                            </div>
                          </td>
                          {/* Order */}
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b', fontSize: 13 }}>
                              <FiHash size={12} />
                              {row.orderReference}
                            </div>
                          </td>
                          {/* Product name */}
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontWeight: 500, color: '#0f172a' }}>{row.name.replace(/\s*\(\d+\s*klipp( igjen)?\)/gi,'').trim()}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>ID: {row.productId}</div>
                          </td>
                          {/* Used / Total */}
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ background: '#f1f5f9', borderRadius: 6, padding: '2px 8px', fontSize: 13, fontWeight: 600, color: '#475569' }}>
                                {row.stampUsed} / {row.stampTotal}
                              </div>
                              {suspicious && (
                                <span title="Antall usedDates stemmer ikke med stampUsed">
                                  <FiAlertTriangle size={14} style={{ color: '#f59e0b' }} />
                                </span>
                              )}
                            </div>
                          </td>
                          {/* Left */}
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ fontWeight: 700, color: row.stampsLeft > 0 ? '#15803d' : '#dc2626', fontSize: 16 }}>
                              {row.stampsLeft}
                            </span>
                          </td>
                          {/* Status */}
                          <td style={{ padding: '12px 14px' }}>{statusBadge(row.status, row.stampsLeft)}</td>
                          {/* Purchased */}
                          <td style={{ padding: '12px 14px', fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' }}>
                            {formatDate(row.datePurchased)}
                          </td>
                          {/* Expand */}
                          <td style={{ padding: '12px 14px' }}>
                            <button
                              onClick={() => toggleExpand(row._rowId)}
                              style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#64748b', fontFamily: 'Poppins' }}
                            >
                              {isOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                              Historikk ({row.redeemHistory.length})
                            </button>
                          </td>
                        </tr>

                        {/* Expanded redeem history */}
                        {isOpen && (
                          <tr style={{ background: '#f8f7ff' }}>
                            <td colSpan={8} style={{ padding: '0 14px 16px 56px' }}>
                              {row.redeemHistory.length === 0 ? (
                                <p style={{ color: '#94a3b8', fontSize: 13, margin: '12px 0' }}>Ingen innløsninger registrert.</p>
                              ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                      {['#','Innløst dato','Klipp brukt','Varighet','Aktivert','Slutt','Billett-ID'].map(h => (
                                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#94a3b8', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row.redeemHistory.map((r, i) => (
                                      <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '7px 10px', color: '#94a3b8' }}>{i + 1}</td>
                                        <td style={{ padding: '7px 10px', color: '#0f172a' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <FiCheckCircle size={12} style={{ color: '#15803d' }} />
                                            {formatDate(r.datePurchased)}
                                          </div>
                                        </td>
                                        <td style={{ padding: '7px 10px', fontWeight: 600 }}>{r.usedStamps ?? 1}</td>
                                        <td style={{ padding: '7px 10px', color: '#6B4FBB' }}>{r.duration ? `${r.duration} min` : '—'}</td>
                                        <td style={{ padding: '7px 10px', color: r.activatedAt ? '#0f172a' : '#94a3b8' }}>
                                          {r.activatedAt ? formatDate(r.activatedAt) : 'Ikke aktivert'}
                                          {r.activatedFrom && (
                                            <span style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>({r.activatedFrom})</span>
                                          )}
                                        </td>
                                        <td style={{ padding: '7px 10px', color: '#0f172a' }}>{formatEndTime(r.activatedAt, r.totalDuration)}</td>
                                        <td style={{ padding: '7px 10px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>{r.id}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}

                              {/* usedDates vs redeemHistory mismatch warning */}
                              {row.usedDates.length !== row.redeemHistory.length && (
                                <div style={{ marginTop: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#92400e', display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <FiAlertTriangle size={14} />
                                  Mismatch: {row.usedDates.length} datostempel(r) i usedDates vs {row.redeemHistory.length} innløsningsdokumenter. Kan indikere misbruk eller synkroniseringsfeil.
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer count */}
          {!loading && (
            <div style={{ marginTop: 16, color: '#94a3b8', fontSize: 13 }}>
              Viser {filtered.length} av {rows.length} klippekort
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
